import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, httpClient } from "@/shared/api/http-client";
import { USER_DETAIL_QUERY_KEY } from "../constants";
import type { UserDetail } from "../types";
import { fetchUserDetail } from "../use-user-detail";
import type {
  OfflineAcceptance,
  RecordOfflineAcceptanceInput,
  RevokeOfflineAcceptanceInput,
} from "./types";

/**
 * Deliberately NOT under `USERS_LIST_QUERY_KEY` (`["admin","users"]`): every
 * user mutation invalidates that prefix, and this permissioned list must not
 * be refetched by unrelated actions.
 */
export const OFFLINE_ACCEPTANCES_QUERY_KEY = ["admin", "user-tos", "offline"] as const;

const JSON_HEADERS = { "content-type": "application/json" } as const;

/** The API said the state the UI shows is already out of date (conflict / gone). */
const STALE_STATE_STATUSES: ReadonlySet<number> = new Set([404, 409]);

function basePath(userId: string): string {
  return `/api/admin/users/${encodeURIComponent(userId)}/tos/offline-acceptances`;
}

async function fetchOfflineAcceptances(userId: string): Promise<readonly OfflineAcceptance[]> {
  const rows = await httpClient<unknown>(basePath(userId));
  if (!Array.isArray(rows)) {
    throw new Error("Offline acceptances response is not a list");
  }
  return rows as readonly OfflineAcceptance[];
}

/**
 * `GET /api/admin/users/:id/tos/offline-acceptances` — the FULL records
 * (reference, note, actors, revocation reason). Requires `tos.offline.manage`
 * (WANDERFIT_ADMIN only), so callers pass `enabled=false` for everyone else
 * rather than provoking a 403. A disabled query would still hand back whatever
 * an earlier session left in the shared cache, so `select` withholds the data
 * entirely when the viewer may not read it.
 */
export function useOfflineAcceptances(userId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: [...OFFLINE_ACCEPTANCES_QUERY_KEY, userId] as const,
    queryFn: () => {
      if (!userId) throw new Error("useOfflineAcceptances requires a userId");
      return fetchOfflineAcceptances(userId);
    },
    enabled: Boolean(userId) && enabled,
    select: (rows) => (enabled ? rows : undefined),
    staleTime: 15_000,
  });
}

function useRefreshAcceptances(userId: string) {
  const queryClient = useQueryClient();
  // Fire-and-forget: the write already succeeded, so a failing refetch must
  // not turn into a failed mutation (the operator would retry and hit a 409).
  // Refetch failures surface through the queries' own error state.
  return () => {
    void queryClient.invalidateQueries({
      queryKey: [...OFFLINE_ACCEPTANCES_QUERY_KEY, userId],
    });
    void queryClient.invalidateQueries({ queryKey: [...USER_DETAIL_QUERY_KEY, userId] });
  };
}

function useMutationRefresh(userId: string) {
  const refresh = useRefreshAcceptances(userId);
  return {
    onSuccess: refresh,
    // 404/409: the server state diverged from what the UI shows (someone else
    // recorded / revoked meanwhile). Re-sync so Revoke / the type list stop
    // offering an action that can only fail again. 400/403/5xx change nothing.
    onError: (err: unknown) => {
      if (err instanceof ApiError && STALE_STATE_STATUSES.has(err.status)) refresh();
    },
  };
}

/** `POST …/offline-acceptances` — record a signed paper for a document TYPE. */
export function useRecordOfflineAcceptance(userId: string) {
  const refresh = useMutationRefresh(userId);
  return useMutation({
    mutationFn: (input: RecordOfflineAcceptanceInput) =>
      httpClient<OfflineAcceptance>(basePath(userId), {
        method: "POST",
        headers: JSON_HEADERS,
        body: JSON.stringify(input),
      }),
    ...refresh,
  });
}

/** `DELETE …/offline-acceptances/:acceptanceId` — revoke (the row is kept). */
export function useRevokeOfflineAcceptance(userId: string) {
  const refresh = useMutationRefresh(userId);
  return useMutation({
    mutationFn: ({ acceptanceId, reason }: RevokeOfflineAcceptanceInput) =>
      httpClient<OfflineAcceptance>(`${basePath(userId)}/${encodeURIComponent(acceptanceId)}`, {
        method: "DELETE",
        headers: JSON_HEADERS,
        body: JSON.stringify({ reason }),
      }),
    ...refresh,
  });
}

/** A readable label for an actor: display name, else email, else unresolved. */
function actorLabel(user: Pick<UserDetail, "displayName" | "email">): string | undefined {
  return user.displayName || user.email || undefined;
}

function combineNames(
  results: readonly { readonly data: string | undefined }[],
  ids: readonly string[],
): ReadonlyMap<string, string> {
  return new Map(results.flatMap((r, i) => (r.data ? ([[ids[i], r.data]] as const) : [])));
}

/**
 * The API records WHO acted only as a user id. Resolve each distinct id to a
 * label for the audit line through the shared user-detail cache entry (the
 * recorder is usually the signed-in admin, so most lookups are already warm);
 * ids that cannot be resolved are absent and the caller falls back to the id.
 */
export function useRecorderNames(ids: readonly string[]): ReadonlyMap<string, string> {
  const distinct = [...new Set(ids)];
  return useQueries({
    queries: distinct.map((id) => ({
      queryKey: [...USER_DETAIL_QUERY_KEY, id] as const,
      queryFn: () => fetchUserDetail(id),
      select: actorLabel,
      staleTime: 5 * 60_000,
      retry: false,
    })),
    combine: (results) => combineNames(results, distinct),
  });
}
